import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Calendar } from "lucide-react";
import { api } from "../lib/api";

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", attempts: 0, data: null });

  const poll = useCallback(async (attempts) => {
    if (!sessionId) {
      setState({ status: "error", attempts: 0, data: null });
      return;
    }
    if (attempts >= 20) {
      setState((s) => ({ ...s, status: "timeout" }));
      return;
    }
    try {
      const { data } = await api.get(`/reservations/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setState({ status: "paid", attempts, data });
        return;
      }
      if (data.status === "expired") {
        setState({ status: "expired", attempts, data });
        return;
      }
      setState({ status: "checking", attempts: attempts + 1, data });
      setTimeout(() => poll(attempts + 1), 1500);
    } catch {
      setTimeout(() => poll(attempts + 1), 1500);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="bg-white min-h-[80vh] flex items-center px-6 py-24">
      <div className="max-w-2xl mx-auto w-full text-center">
        {state.status === "checking" && (
          <div data-testid="success-checking">
            <Loader2 className="w-16 h-16 text-fegreen mx-auto animate-spin mb-8" />
            <h1 className="text-3xl font-bold text-fegray-dark mb-4">Vérification du paiement…</h1>
            <p className="text-fegray">Merci de patienter, nous confirmons votre réservation auprès de Stripe.</p>
          </div>
        )}

        {state.status === "paid" && (
          <div data-testid="success-paid">
            <div className="w-24 h-24 bg-fegreen-light rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle2 className="w-12 h-12 text-fegreen" strokeWidth={1.8} />
            </div>
            <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-4">— Confirmation</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-fegray-dark mb-6">
              Paiement <span className="text-fegreen">confirmé !</span>
            </h1>
            <p className="text-fegray text-lg mb-10 max-w-md mx-auto leading-relaxed">
              Votre réservation est enregistrée. Rosalie vous contactera dans
              les 24h pour confirmer le créneau et préparer votre séance.
            </p>

            <div className="fe-card p-6 text-left mb-10 max-w-md mx-auto">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-fegray uppercase tracking-wider">Montant payé</div>
                  <div className="text-2xl font-extrabold text-fegreen mt-1">
                    {state.data?.amount_total ? (state.data.amount_total / 100).toFixed(2) : "—"}€
                  </div>
                </div>
                <div>
                  <div className="text-xs text-fegray uppercase tracking-wider">Devise</div>
                  <div className="text-2xl font-extrabold text-fegray-dark mt-1">
                    {state.data?.currency?.toUpperCase() || "EUR"}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/" className="btn-green" data-testid="success-home-btn">
                Retour à l&apos;accueil <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="btn-outline-green">
                <Calendar className="w-4 h-4" /> Modifier ma séance
              </Link>
            </div>
          </div>
        )}

        {(state.status === "timeout" || state.status === "expired" || state.status === "error") && (
          <div data-testid="success-error">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-8" />
            <h1 className="text-3xl font-bold text-fegray-dark mb-4">
              {state.status === "timeout" ? "Vérification trop longue" : "Paiement non finalisé"}
            </h1>
            <p className="text-fegray mb-10">
              Si vous pensez qu&apos;il y a une erreur, contactez-nous directement.
            </p>
            <Link to="/reservation" className="btn-green">Réessayer</Link>
          </div>
        )}
      </div>
    </div>
  );
}
